import { alpha, styled } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { Link } from "react-router-dom";
import useSidebar from "../SidebarProvider/useSidebar";
import MenuItemLink from "components/MenuItemLink";

export const Section = styled("section")({});
export const StyledLink = styled(Link)({});

interface NavProps {
  children: React.ReactNode;
}

export const Nav = styled((props: NavProps) => {
  const { open } = useSidebar();
  return <nav data-sidebar="sidebar" data-open={open} {...props} />;
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  width: "256px",
  backgroundColor: theme.palette.primary.main,
  transition: "width 200ms",
  willChange: "width",
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",

  "&[data-open='false']": {
    minWidth: "min-content",
    width: "52px",
  },
}));

export const RouteItemLink = styled(Link)(({ theme }) => ({
  padding: "4px 8px",
  gap: "14px",
  display: "flex",
  alignItems: "center",
  minHeight: "28px",
  borderRadius: "4px",
  cursor: "pointer",
  color: "white",
  textDecoration: "none",
  width: "100%",

  "&:hover": {
    backgroundColor: alpha(theme.palette.grey[120], 0.5),
  },

  "&[data-active='true']": {
    backgroundColor: alpha(theme.palette.secondary1[60], 0.2),
    color: theme.palette.secondary1[60],
    ".MuiSvgIcon-root": {
      color: theme.palette.secondary1[60],
    },
  },
}));

/**
 * Refer to RouteItemLink for the styles
 */
export const SecondaryItem = styled("span")(({ theme }) => ({
  padding: "4px 8px",
  gap: "14px",
  display: "flex",
  alignItems: "center",
  minHeight: "28px",
  borderRadius: "4px",
  cursor: "pointer",
  color: theme.palette.grey[60],
  textDecoration: "none",
  width: "100%",

  "&:hover": {
    color: "white",
    backgroundColor: alpha(theme.palette.grey[120], 0.5),
    ".MuiSvgIcon-root": {
      color: "white",
    },
  },
}));

/**
 * Refer to RouteItemLink for the styles
 */
export const SettingsMenuButton = styled("button")(({ theme }) => ({
  all: "unset",
  display: "flex",
  overflow: "hidden",
  width: "100%",
}));

export const SettingsMenuButtonContent = styled("div")(({ theme }) => ({
  display: "flex",
  width: "100%",
  borderRadius: "8px",
  gap: "14px",
  alignItems: "center",
  justifyContent: "center",

  "&[data-open='true']:hover": {
    backgroundColor: alpha(theme.palette.grey[120], 0.5),
  },

  "&[data-open='true']": {
    flex: 1,
    overflow: "hidden",
    padding: "4px 8px",
    "&[data-active='true']": {
      backgroundColor: alpha(theme.palette.secondary1[60], 0.2),
      ".MuiSvgIcon-root": {
        color: theme.palette.secondary1[60],
      },
    },
  },

  "&:hover": { cursor: "pointer" },

  "&[data-open='false']": {
    "&:focus": {
      outline: `3px solid ${alpha(theme.palette.secondary1[60], 0.2)}`,
      outlineOffset: "-1px",
    },
  },
}));

export const LogoNavigationContainer = styled(Section)(({ theme }) => ({
  display: "flex",
  position: "relative",
  width: "100%",
  padding: theme.spacing(2, 2, 1.5, 2),
  gap: theme.spacing(1),
  transition: "flexDirection 200ms",

  "&[data-open='false']": {
    flexDirection: "column",
    padding: theme.spacing(2, 1.5, 0.75, 1.5),
  },
}));

export const LogoNavigation = styled(Link)(({ theme }) => ({
  width: "100%",
  position: "relative",
  minHeight: "26px",
}));

export const Logo = styled("img")(() => ({
  display: "flex",
  objectFit: "contain",
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  transition: "opacity 350ms",
  opacity: 1,

  "&[data-open='false']": {
    opacity: 0,
  },

  "&[data-size='small']": {
    left: "50%",
    transform: "translate(-50%, -50%)",
    height: "20px",
    width: "30px",
  },

  "&[data-size='large']": {
    width: "158px",
    height: "26px",
  },
}));

export const SidebarGroup = styled("div")(() => ({
  display: "flex",
  padding: "8px",
}));

export const SettingsIconContainer = styled("div")(({ theme }) => ({
  color: "white",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "28px",
  height: "28px",

  "&[data-active='true']": {
    backgroundColor: alpha(theme.palette.secondary1[60], 0.2),
    color: theme.palette.secondary1[60],
    borderRadius: "4px",
  },
}));

export const SettingMenuItemLink = styled(MenuItemLink)(({ theme }) => ({
  textDecoration: "none",
  color: "inherit",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "6px 12px",
  width: "100%",
  fontSize: theme.typography.TextS.fontSize,
  lineHeight: theme.typography.TextM.lineHeight,
}));

export const SettingsMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: theme.typography.TextS.fontSize,
  fontWeight: theme.typography.TextS.fontWeight,
  lineHeight: theme.typography.TextM.lineHeight,
  padding: "0px",
}));

export const SidebarExpandCollapsePosition = styled("div")(({ theme }) => ({
  position: "absolute",
  top: "50%",
  bottom: "50%",
  right: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  "&[data-open='false']": {
    right: "-4px",
    top: "50%",
    bottom: "4px",
  },

  "& > div": {
    zIndex: theme.zIndex.drawer,
    background: theme.palette.primary.main,
    borderRadius: "50%",
  },
}));
